import get from 'lodash/get';
import flow from 'lodash/flow';

import { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'clsx';
import { connect } from 'react-redux';

import { updateAuthViewType as triggerAuthViewChange } from 'src/common/actions/auth';

import {
  AUTH_PAIRING_FLOW_TI_FOR_FREE_HEADER,
  AUTH_SIGN_UP_TITLE,
  AUTH_SIGN_IN_TITLE,
  FORGOT_PASSWORD_INSTRUCTIONS,
  FORGOT_PASSWORD_TITLE,
  RESET_PASSWORD_INSTRUCTIONS,
  RESET_PASSWORD_TITLE,
  SIGN_IN,
  SIGN_IN_AGREEMENT,
  SIGN_IN_SIGN_UP_SUBTITLE,
  SIGN_IN_SIGN_UP_TITLE,
  SIGN_UP_AGREEMENT,
  VERIFY_SUBTITLE,
} from 'src/common/constants/localizations/auth';
import { selectIsMobile } from 'src/common/selectors/app';
import PaymentFlowFormLayout from '../payment/PaymentFlowFormLayout';
import AuthAndPaymentFlowHeader from '../shared/AuthAndPaymentFlowHeader';
import { PAYMENT_FLOW } from '../../constants/subscription/flow';
import viewTypes, { viewTypeAttributes } from '../../constants/auth/viewTypes';
import { isPairAuthFlowPin } from '../../utils/auth/pairAuthFlowHelpers';
import getOverrideOrDefaultResourceKey from '../../utils/getOverrideOrDefaultResourceKey';
import FakePaymentForm from '../payment/FakePaymentForm';
import isPaymentRequired from '../utils/isPaymentRequired';
import withAnimationControl from '../../decorators/withAnimationControl';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import SignInView from './SignInView';
import SignUpView from './SignUpView';
import ForgotPassword from './ForgotPassword';
import ResetPassword from './ResetPassword';
import LogoutFailure from './shared/LogoutFailure';
import AuthFlowLayout from './shared/AuthFlowLayout';
import AuthViewFooter from './shared/AuthViewFooter';
import AuthFlowProgressBar from './shared/AuthFlowProgressBar';

import css from './auth-view-container.module.scss';
import authCss from './auth.module.scss';

/*
  The AuthViewContainer is used in OauthAuthPage.js AuthPage, and AuthDialog.js
*/
class AuthViewContainer extends Component {
  static propTypes = {
    isMobile: PropTypes.bool.isRequired,
    updateAuthViewType: PropTypes.func.isRequired,
    isDialog: PropTypes.bool,
    isPartner: PropTypes.bool,
    queryProduct: PropTypes.object,
    products: PropTypes.array,
    breakpoint: PropTypes.number,
    formContainerClassName: PropTypes.string,
    resetFormPadding: PropTypes.bool,
    authActions: PropTypes.object.isRequired,
    // from withAnimationControl
    isRecurlyLoading: PropTypes.bool.isRequired,
    setRecurlyLoadingState: PropTypes.func.isRequired,
    // from withAuthViewHandling
    history: PropTypes.object.isRequired,
    loginDetails: PropTypes.object.isRequired,
    source: PropTypes.string,
    viewType: PropTypes.string.isRequired,
  };

  static contextType = LocationAndLocalizationContext;

  static defaultProps = {
    formContainerClassName: '',
  };

  constructor(props) {
    super(props);

    this.authViewContainerRef = createRef();
  }

  componentDidMount() {
    // Always apply focus to the container, for keyboard accessibility. However, don't do this for
    // mobile devices
    if (!this.props.isMobile) {
      this.authViewContainerRef.current.focus();
    }
  }

  getViewToRender() {
    const {
      authActions,
      loginDetails: { failAuthNotice, successAuthNotice },
      isDialog,
      isPartner,
      history,
      queryProduct,
      products,
      breakpoint,
      isRecurlyLoading,
      setRecurlyLoadingState,
      viewType,
      source,
      updateAuthViewType,
      formContainerClassName,
      resetFormPadding = false,
    } = this.props;
    const { getLocalizedText, location } = this.context;
    const requiresPayment = isPaymentRequired(queryProduct, location);

    // We don't want to render the payment flow layout for an auth view if it is
    // in a dialog.
    const isPaymentFlow = !!location.query[PAYMENT_FLOW] && !isDialog;
    const query = get(location, 'query', {});
    const hasPinCodeParam = isPairAuthFlowPin(query);
    const isPinCodePairingFlow = isPartner && hasPinCodeParam;

    const commonProps = {
      actions: authActions,
      failAuthNotice,
      successAuthNotice,
      isDialog,
      history,
      isPaymentFlow,
      viewTypeData: {
        viewType,
        attribute: isDialog
          ? viewTypeAttributes.dialog
          : viewTypeAttributes.fullPage,
      },
    };

    const layoutProps = {
      alwaysShowUpsellTable: true,
      selectedProduct: queryProduct,
      isPaymentFlow,
      isNoPaymentFlow: !requiresPayment,
      formContainerClassName,
      resetFormPadding,
    };

    const fakePaymentFormProps = {
      breakpoint,
      products,
      selectedProduct: queryProduct,
      setRecurlyLoadingState,
      isRecurlyLoading,
    };

    // if in a dialog, the recurly code will not get executed to set its loading state
    // since FakeAuthPaymentForm is gated in isPaymentFlow
    // so we will then just show the footer when not in payment flow,
    // otherwise we check for loading state
    // Additionally, the isPartner flow uses a layout that handles it's own footer
    const shouldShowAuthFooter =
      !isPartner && (!isPaymentFlow || !isRecurlyLoading);

    const generateSignInSignUpViewContent = (mainContent) => {
      const isSignUpPage = viewType === viewTypes.signUp;
      let defaultHeaderKey = isSignUpPage
        ? AUTH_SIGN_UP_TITLE
        : AUTH_SIGN_IN_TITLE;
      defaultHeaderKey = isPaymentFlow
        ? defaultHeaderKey
        : SIGN_IN_SIGN_UP_TITLE;

      return (
        <>
          {isPinCodePairingFlow && (
            <div
              data-testid={
                isSignUpPage ? 'signUpPageProgressBar' : 'signInPageProgressBar'
              }
              className={css.progressBarContainer}
            >
              <AuthFlowProgressBar step={2} />
            </div>
          )}
          <AuthAndPaymentFlowHeader
            isPartnerFlow={isPartner}
            header={
              isPartner
                ? AUTH_PAIRING_FLOW_TI_FOR_FREE_HEADER
                : getOverrideOrDefaultResourceKey(
                    getLocalizedText,
                    location,
                    defaultHeaderKey,
                  )
            }
            subheader={
              isPartner
                ? ''
                : getOverrideOrDefaultResourceKey(
                    getLocalizedText,
                    location,
                    SIGN_IN_SIGN_UP_SUBTITLE,
                  )
            }
          />
          {mainContent}
          {isPaymentFlow && <FakePaymentForm {...fakePaymentFormProps} />}
          {shouldShowAuthFooter && (
            <AuthViewFooter
              agreementText={
                isSignUpPage
                  ? getOverrideOrDefaultResourceKey(
                      getLocalizedText,
                      location,
                      SIGN_UP_AGREEMENT,
                    )
                  : SIGN_IN_AGREEMENT
              }
              source={source}
            />
          )}
        </>
      );
    };

    switch (viewType) {
      case viewTypes.signUp: {
        const signUpView = (
          <SignUpView
            key="signUpView"
            handleChangeToSignIn={() => updateAuthViewType(viewTypes.signIn)}
            isPartner={isPartner}
            source={source}
            {...commonProps}
          />
        );
        const generatedViewContent =
          generateSignInSignUpViewContent(signUpView);

        return isPartner ? (
          <AuthFlowLayout>
            <div className={css.partnerAuthContent}>{generatedViewContent}</div>
          </AuthFlowLayout>
        ) : (
          <PaymentFlowFormLayout {...layoutProps}>
            <div>{generatedViewContent}</div>
          </PaymentFlowFormLayout>
        );
      }
      case viewTypes.forgotPassword:
        return (
          <PaymentFlowFormLayout {...layoutProps}>
            <div>
              <AuthAndPaymentFlowHeader
                header={FORGOT_PASSWORD_TITLE}
                subheader={FORGOT_PASSWORD_INSTRUCTIONS}
              />
              <ForgotPassword
                key="forgotPasswordView"
                handleChangeToSignIn={() =>
                  updateAuthViewType(viewTypes.signIn)
                }
                {...commonProps}
              />
            </div>
          </PaymentFlowFormLayout>
        );
      case viewTypes.resetPassword:
        return (
          <PaymentFlowFormLayout {...layoutProps}>
            <div>
              <AuthAndPaymentFlowHeader
                header={RESET_PASSWORD_TITLE}
                subheader={RESET_PASSWORD_INSTRUCTIONS}
              />
              <ResetPassword
                key="forgotPasswordView"
                handleChangeToSignIn={() =>
                  updateAuthViewType(viewTypes.signIn)
                }
                {...commonProps}
              />
            </div>
          </PaymentFlowFormLayout>
        );
      case viewTypes.logoutFailure:
        return <LogoutFailure key="logoutFailureView" />;
      case viewTypes.verify:
        return (
          <PaymentFlowFormLayout {...layoutProps}>
            <div>
              <AuthAndPaymentFlowHeader
                header={SIGN_IN}
                subheader={VERIFY_SUBTITLE}
              />
              <SignInView
                key="signInView"
                source={source}
                isVerifyFlow
                {...commonProps}
              />
            </div>
          </PaymentFlowFormLayout>
        );
      default: {
        const signInView = (
          <SignInView
            key="signInView"
            handleChangeToSignUp={() => updateAuthViewType(viewTypes.signUp)}
            handleChangeToForgotPassword={() =>
              updateAuthViewType(viewTypes.forgotPassword)
            }
            isPartner={isPartner}
            source={source}
            {...commonProps}
          />
        );
        const generatedViewContent =
          generateSignInSignUpViewContent(signInView);

        return isPartner ? (
          <AuthFlowLayout>
            <div className={css.partnerAuthContent}>{generatedViewContent}</div>
          </AuthFlowLayout>
        ) : (
          <PaymentFlowFormLayout {...layoutProps}>
            <div>{generatedViewContent}</div>
          </PaymentFlowFormLayout>
        );
      }
    }
  }

  render() {
    const { isDialog } = this.props;

    return (
      <div
        ref={this.authViewContainerRef}
        data-testid="authViewContainer"
        tabIndex="0"
        className={classNames(css.container, authCss.withFormSections, {
          [authCss.fullScreen]: !isDialog,
        })}
      >
        {this.getViewToRender()}
      </div>
    );
  }
}

export const mapStateToProps = (state) => ({
  isMobile: selectIsMobile(state),
});

function mapDispatchToProps(dispatch) {
  return {
    updateAuthViewType: (values) => dispatch(triggerAuthViewChange(values)),
  };
}

export default flow(
  withAnimationControl,
  connect(mapStateToProps, mapDispatchToProps),
)(AuthViewContainer);
